import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from"../../Config/config";
import { unlockAllClicks } from"../../utils/mockInterviewClickTracker";
import { confirmPayment } from"../../utils/paymentStatus";

export default function SubscriptionPlans() {
 const navigate = useNavigate();
 const [notice, setNotice] = useState("");
 const [selectedPlanId, setSelectedPlanId] = useState(null);
 const [user, setUser] = useState(null);

 const plans = [
 { id: 'monthly', price: 2999, label: 'Basic (Monthly)', tagline: 'Perfect for getting started', duration: '1 month' },
 { id: 'halfyear', price: 7999, label: 'Standard (3 Months)', tagline: 'Most popular choice', duration: '3 months' },
 { id: 'yearly', price: 14999, label: 'Premium (6 Months)', tagline: 'Best value for long-term learners', duration: '6 months' }
 ];

 const features = [
 'Mock Interviews',
 'LLM Assistant',
 'All 5 dashboards',
 'TheSkillGenie (TSG) Your AI Assistant and Mentor',
 'Launchpad Technologies Docs & MCQs',
 'Compiler & Take Challenges'
 ];

 // Helper to format date to IST
 const formatDateToIST = (dateString) => {
 const date = new Date(dateString);
 const options = {
 timeZone: 'Asia/Kolkata',
 year: 'numeric',
 month: 'long',
 day: 'numeric',
 hour: '2-digit',
 minute: '2-digit',
 second: '2-digit',
 };
 return date.toLocaleString('en-IN', options);
 };

 // Load Razorpay script
 const loadRazorpayScript = () => {
 return new Promise((resolve) => {
 const script = document.createElement('script');
 script.src ="https://checkout.razorpay.com/v1/checkout.js";
 script.onload = () => resolve(true);
 script.onerror = () => resolve(false);
 document.body.appendChild(script);
 });
 };

 // On mount, get user from localStorage
 useEffect(() => {
 const storedUser = JSON.parse(localStorage.getItem('user'));
 setUser(storedUser);
 }, []);

 // Checkout logic
 const startCheckout = async (planId) => {
 const plan = plans.find(p => p.id === planId);
 if (!plan) return;
 setSelectedPlanId(planId);
 setNotice(`Selected: ${plan.label} (₹${(plan.price || 0).toLocaleString('en-IN')}) for ${plan.duration}`);

 if (!user) {
 alert("Please log in to continue with the subscription.");
 return;
 }

 const res = await loadRazorpayScript();
 if (!res) {
 alert("Razorpay SDK failed to load. Please check your internet connection.");
 return;
 }

 try {
 // Step 1: Create order on backend
 const createOrderRes = await axios.post(`${API_BASE_URL}/api/skillgenie-premium/create-order`, {
 amount: plan.price,
 planName: plan.label
 });

 if (!createOrderRes.data.ok) {
 alert(createOrderRes.data.error ||"Failed to create order.");
 return;
 }

 const { id: order_id, amount, currency, endDate } = createOrderRes.data.order;
 const keyId = createOrderRes.data.keyId;
 
 if (!keyId) {
 alert("Failed to get Razorpay key from backend. Please try again.");
 return;
 }

 // Step 2: Open Razorpay checkout
 const options = {
 key: keyId,
 amount: amount.toString(),
 currency,
 name:"SkillGenie Subscription",
 description: plan.label,
 image:"/logo.png",
 order_id,
 handler: async function (response) {
 try {
 const verifyRes = await axios.post(`${API_BASE_URL}/api/skillgenie-premium/verify-payment`, {
 razorpay_order_id: response.razorpay_order_id,
 razorpay_payment_id: response.razorpay_payment_id,
 razorpay_signature: response.razorpay_signature,
 planName: plan.label,
 amount: plan.price,
 user_id: user.userId,
 status: 'success',
 endDate // Pass endDate to backend
 });

 if (verifyRes.data.ok) {
 // ✅ Confirm payment and unlock ALL features
 confirmPayment(user.userId, {
 planName: plan.label,
 amount: plan.price,
 expiryDate: endDate,
 paymentMethod: 'razorpay',
 orderId: response.razorpay_order_id,
 paymentId: response.razorpay_payment_id
 });
 
 // ✅ Unlock all mock interview clicks
 unlockAllClicks(user.userId);
 
 alert(`Payment successful! Your subscription (${plan.label}) is active until ${formatDateToIST(endDate)}.\n\nFull access unlocked: MCQs, Challenges, LLM, Mock Interviews, All Dashboards`);
 
 // Store subscription details in localStorage (backup)
 localStorage.setItem('subscription', JSON.stringify({
 planName: plan.label,
 endDate,
 amount: plan.price,
 startDate: new Date().toISOString(),
 paymentStatus: 'confirmed'
 }));
 
 navigate('/dashboard');
 } else {
 alert("Payment verification failed!");
 }
 } catch (verifyErr) {
 console.error("Verification error:", verifyErr);
 alert("Payment verification failed due to an error.");
 }
 },
 prefill: {
 name: `${user.first_name} ${user.last_name}`,
 email: user.email,
 },
 notes: {
 planName: plan.label,
 endDate
 },
 theme: {
 color:"var(--primary-blue, #4F46E5)",
 }
 };

 const rzp = new window.Razorpay(options);
 rzp.open();

 } catch (err) {
 console.error("Payment error:", err);
 alert(err.response?.data?.error ||"Something went wrong while initiating payment.");
 }
 };

 return (
 <div className="min-h-screen bg-bgMain text-body font-sans relative overflow-hidden">
 {/* Background decoration */}
 <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
 
 <main className="max-w-6xl mx-auto px-6 py-16 relative z-10">
 <button
 onClick={() => navigate('/dashboard')}
 className="bg-bgSection text-primary px-6 py-2 rounded-full font-bold border border-primary/30 hover:bg-primary hover:text-white transition-all shadow-lg mb-10 flex items-center gap-2 uppercase text-xs tracking-widest"
 >
 ← Back to Dashboard
 </button>

 <div className="text-center mb-16">
 <h2 className="text-5xl md:text-6xl font-black mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent uppercase tracking-tighter">Choose Your Plan</h2>
 <p className="text-muted max-w-2xl mx-auto text-lg md:text-xl font-medium leading-relaxed">
 All plans include the full feature package for their respective durations.
 </p>
 </div>

 {notice && (
 <div className="mb-8 rounded-2xl border border-primary/30 bg-primary/10 px-6 py-4 text-sm font-bold text-primary shadow-2xl animate-fadeIn">
 {notice}
 </div>
 )}

 <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
 {plans.map((p) => (
 <div
 key={p.id}
 className={`rounded-[32px] border-2 ${selectedPlanId === p.id ? 'border-primary shadow-[0_0_40px_rgba(79, 70, 229,0.3)]' : 'border-accentGray/30'} 
 bg-white p-8 flex flex-col shadow-2xl transition-all hover:scale-[1.03] hover:border-primary/50 relative group overflow-hidden`}
 >
 {/* Card background glow */}
 <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-all"></div>

 <div className="mb-6 relative z-10">
 <div className="text-xs text-primary font-bold mb-4 uppercase tracking-widest">{p.tagline}</div>
 <div className="text-5xl font-black text-[#1a1a1a] tracking-tighter">₹{p.price.toLocaleString('en-IN')}</div>
 <div className="text-primary text-sm font-bold uppercase tracking-widest mt-2">{p.label} • {p.duration}</div>
 </div>

 <ul className="text-sm text-gray-700 space-y-3 flex-1 mt-4 relative z-10">
 {features.map((f, i) => (
 <li key={i} className="flex items-start gap-3">
 <span className="text-primary mt-1">✓</span>
 <span>{f}</span>
 </li>
 ))}
 </ul>

 <button
 onClick={() => startCheckout(p.id)}
 className="mt-8 w-full btn-primary-gradient py-4 rounded-2xl font-black text-lg shadow-xl hover:scale-105 active:scale-95 transition-all uppercase tracking-widest"
 >
 Continue
 </button>
 </div>
 ))}
 </div>

 <div className="rounded-[32px] border border-accentGray/30 bg-bgSection p-10 text-sm text-muted text-center shadow-2xl relative overflow-hidden">
 <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
 <p className="font-medium relative z-10">
 By subscribing, you agree to our{' '}
 <a href="/terms-of-service" className="text-primary font-bold underline hover:text-primary transition">Terms of Service</a>
 {' '}and{' '}
 <a href="/privacy-policy" className="text-primary font-bold underline hover:text-primary transition">Privacy Policy</a>.
 </p>
 </div>
 </main>
 </div>
 );
}




